import React, { useEffect } from 'react';
import { DownCircleFilled } from '@ant-design/icons';
import './Styles.scss';
import { Dropdown, Space, message } from 'antd';
import { useDispatch } from 'react-redux';
import { getData } from '../../../actions/apiCall';
import { putData } from '../../../actions/developer';
import moment from 'moment';

const StageStepper: React.FC<{ active: string | null, dealId: string | null, deal: any, onStageUpdate?: () => Promise<void> }> = ({ active, dealId, deal, onStageUpdate }) => {
  const [stages, setStages] = React.useState<{ _id: string, name: string }[]>([]);
  const [activeStage, setActiveStage] = React.useState<string | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    setActiveStage(active);
  }, [active, deal?.currentStage?._id]);

  const onActive = (stage: string) => {
    handleProbalityChange(dealId, stage);
  }

  const handleProbalityChange = async (id, stage) => {
    try {
      if (!id) {
        message.error('Deal ID is missing');
        return;
      }
      if (!stage) {
        message.error('Stage ID is missing');
        return;
      }

      const resData = await dispatch(putData(`/deals/${id}`, { currentStage: stage }));

      // putData returns response.data.data when code is 200, otherwise response.data
      if (resData && resData._id) {
        // Success - update local state immediately
        setActiveStage(stage);
        message.success('Stage updated successfully');
        // Call callback to refresh parent component if provided
        if (onStageUpdate) {
          await onStageUpdate();
        }
      } else if (resData && resData.code && resData.code !== 200) {
        message.error(resData.message || 'Failed to update stage');
        console.error('Failed to update stage:', resData);
      } else {
        message.error('Failed to update stage');
        console.error('Invalid response format:', resData);
      }
    } catch (error: any) {
      console.error('Error updating stage:', error);
      message.error(error?.message || 'Error updating stage');
    }
  };

  React.useEffect(() => {
    getStates();
  }, []);

  const getStates = async () => {
    try {
      if (!deal?.leadType?._id) {
        return;
      }
      const response = await dispatch(getData(`/stages?category=Deals&leadType=${deal?.leadType?._id}&andOr=and`));
      if (response.code === 200) {
        // Sort stages by order (numeric) to ensure correct display order
        const sortedStages = [...(response.data || [])].sort((a, b) => {
          const orderA = parseInt(a.order) || 0;
          const orderB = parseInt(b.order) || 0;
          return orderA - orderB;
        });
        setStages(sortedStages);
      } else {
        console.error('Failed to fetch stages:', response.message);
        return [];
      }
    } catch (error) {
      console.error('Error fetching stages:', error);
      return [];
    }
  }

  return (
    <div className='bg-white rounded-md mb-6 p-4 flex flex-col justify-center item-center'>
      <div className='flex justify-between w-full'>
        <Space direction='vertical'>
          <span className='text-gray-500'>START</span>
          <span className='text-gray-900'>
            {deal?.createdAt ? moment(deal.createdAt).format('DD/MM/YYYY') : 'N/A'}
          </span>
        </Space>
        <Space direction='vertical'>
          <span className='text-gray-500'>CLOSING</span>
          <span className='text-gray-900'>
            {deal?.closingDate ? moment(deal.closingDate).format('DD/MM/YYYY') : 'N/A'}
          </span>
        </Space>
      </div>
      <div className="flex items-center w-full p-4">
        <div className="flex w-full border border-gray-300 bg-gray-300 rounded-full overflow-hidden">
          {stages.map((stage, index) => {
            const isActive = stage?._id === activeStage;
            const isFirst = index === 0;
            const isLast = index === stages.length - 1;

            return (
              <div
                key={stage._id}
                onClick={() => onActive(stage._id)}
                className={`
                  relative flex items-center justify-center text-sm font-medium px-4 py-2 -mr-[11px] flex-1 cursor-pointer
                  ${isActive ? 'bg-indigo-600 text-white' : 'bg-white text-gray-900'}
                  ${!isLast ? 'border-r border-gray-300' : ''}
                  step-clip
                  ${isFirst ? 'step-clip-first' : ''}
                  ${isLast ? 'step-clip-last' : ''}
                `}
              >
                <span>{stage.name}</span>
                {isActive && (
                  <Dropdown
                    menu={{
                      items: stages.map((s, i) => ({ key: s._id, label: s.name })),
                      onClick: (e: any) => {
                        e.domEvent?.stopPropagation(); // Prevent triggering parent onClick
                        onActive(e.key);
                      }
                    }}
                    trigger={['click']}
                    overlayClassName="stage-dropdown"
                  >
                    <DownCircleFilled
                      className="ml-2 cursor-pointer"
                      onClick={(e) => e.stopPropagation()} // Prevent triggering parent onClick
                    />
                  </Dropdown>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StageStepper;
